/**
 * SuperBrain Dashboard - Main Chat Interface
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';
import MarginHealthWidget from './MarginHealthWidget';
import ProdDriftBadge from './ProdDriftBadge';
import TrialUsageWidget from './TrialUsageWidget';
import MonthlyUsageWidget from './MonthlyUsageWidget';
import ReferralLeaderboard from './ReferralLeaderboard';
import LanguageSwitcher from './LanguageSwitcher';
import UseDisclaimerBanner from './UseDisclaimerBanner';
import MakeEveryQueryCountTip from './MakeEveryQueryCountTip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(user);
  const [activeModels, setActiveModels] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('comment');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [maxAccuracyMode, setMaxAccuracyMode] = useState(false);
  const [fullPowerMode, setFullPowerMode] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [referral, setReferral] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchUserInfo();
    fetchActiveModels();
    fetchSessions();
    fetchReferral();
  }, []);

  const fetchReferral = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/referrals/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferral(r.data);
    } catch (e) { /* silent */ }
  };

  const copyReferralLink = async () => {
    if (!referral) return;
    const url = `${window.location.origin}${referral.share_path}`;
    try {
      await navigator.clipboard.writeText(url);
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 2500);
    } catch (e) {
      alert(url);
    }
  };

  const openBillingPortal = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/subscriptions/customer-portal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = r.data.portal_url;
    } catch (e) {
      const msg = e.response?.data?.detail || 'Billing portal unavailable. Email a1affiliatemarketingllc@gmail.com for help.';
      alert(msg);
    }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(r.data.sessions || []);
    } catch (e) { /* silent */ }
  };

  const loadSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/chat/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loaded = (r.data.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        providers: m.models_used,
        accuracyScore: m.accuracy_score,
        accuracyLabel: m.accuracy_label,
        dissentFlags: m.dissent_flags || [],
        maxAccuracyMode: m.max_accuracy_mode,
        timestamp: m.timestamp
      }));
      setMessages(loaded);
      setCurrentSessionId(sessionId);
    } catch (e) { alert('Failed to load conversation'); }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentSessionId(null);
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chat/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (currentSessionId === sessionId) startNewChat();
      fetchSessions();
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserInfo(response.data.user);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  };

  const fetchActiveModels = async () => {
    try {
      const response = await axios.get(`${API}/health`);
      // All 6 AI providers are active and configured
      setActiveModels([
        { name: 'Claude', status: 'active', color: '#6366f1' },
        { name: 'GPT-5', status: 'active', color: '#10a37f' },
        { name: 'Gemini', status: 'active', color: '#4285f4' },
        { name: 'Grok', status: 'active', color: '#000000' },
        { name: 'Perplexity', status: 'active', color: '#20808d' },
        { name: 'Mistral', status: 'active', color: '#ff7000' }
      ]);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chat`,
        {
          message: input,
          max_accuracy_mode: maxAccuracyMode,
          force_full_power: fullPowerMode,
          session_id: currentSessionId,
          language: (i18n.language || 'en').split('-')[0]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      if (data.session_id) setCurrentSessionId(data.session_id);
      const synth = data.synthesis_info || {};
      const assistantMessage = {
        role: 'assistant',
        content: data.final_answer || data.response,
        providers: data.routing_info?.models_queried || data.providers_queried || synth.citations,
        accuracyScore: synth.accuracy_score,
        accuracyLabel: synth.accuracy_label,
        dissentFlags: synth.dissent_flags || [],
        maxAccuracyMode: synth.max_accuracy_mode,
        consensus: synth.consensus,
        divergences: synth.divergences,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      fetchUserInfo(); // Refresh usage stats
      fetchSessions();
    } catch (error) {
      // 402 Payment Required → card missing; redirect to Stripe Checkout via /pricing
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 402 && detail && typeof detail === 'object' && detail.code === 'card_required') {
        const friendly = {
          role: 'error',
          content: (detail.message || 'A payment method is required.') +
                   ' Redirecting to add a card…',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, friendly]);
        setTimeout(() => {
          window.location.href = detail.upgrade_url || '/pricing';
        }, 1200);
        return;
      }
      const errorMessage = {
        role: 'error',
        content:
          typeof detail === 'string'
            ? detail
            : (detail && detail.message) || 'Failed to get response',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
  };

  const submitFeedback = async () => {
    if (!feedbackMessage.trim()) {
      alert('Please enter your feedback');
      return;
    }

    setFeedbackSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/feedback/submit`,
        {
          message: feedbackMessage,
          type: feedbackType
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Thank you for your feedback! The admin will review it soon.');
      setFeedbackMessage('');
      setFeedbackType('comment');
      setShowFeedbackModal(false);
    } catch (error) {
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };


  const getPlanBadge = () => {
    // Admin / commander accounts get the COMMANDER badge — Plan and badge must
    // agree (audit iter 7 flagged the legacy "PRO AGENTIC AGGREGATE" copy here).
    if (userInfo.is_admin || userInfo.plan === 'commander') {
      return { text: 'COMMANDER', color: '#C8860A' };
    }
    if (userInfo.plan === 'trial') {
      return { text: 'TRIAL', color: '#C8860A' };
    }
    // Public-facing tier names map 1:1 to badge labels.
    const tierMap = {
      professional: 'PROFESSIONAL',
      business:     'BUSINESS',
      enterprise:   'ENTERPRISE',
      ultimate:     'ULTIMATE',
    };
    const label = tierMap[userInfo.plan] || (userInfo.plan || '').toUpperCase() || 'PLAN';
    return { text: label, color: '#2B5FA8' };
  };

  const badge = getPlanBadge();

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <div className="dashboard-sidebar">
        <div className="sidebar-header">
          <h2>SUPERBRAIN</h2>
          <p className="sidebar-subtitle">AGENTIC AGGREGATE</p>
        </div>

        <div className="user-info-card">
          <div className="user-email">{userInfo.email}</div>
          <div 
            className="user-plan-badge" 
            style={{ backgroundColor: badge.color }}
          >
            {badge.text}
          </div>
          <div className="usage-stats">
            <p>Plan: <strong>{userInfo.plan?.toUpperCase()}</strong></p>
            <p>Usage: <strong>{userInfo.usage || 0} / {userInfo.limit || 0}</strong></p>
            <p>Remaining: <strong>{userInfo.remaining || 0}</strong></p>
          </div>

          <TrialUsageWidget tierStatus={userInfo.tier_status} />
          <MonthlyUsageWidget monthlyUsage={userInfo.monthly_usage} />
          <ReferralLeaderboard />

          {/* Admin-only link */}
          {userInfo.email && userInfo.email.toUpperCase() === 'A1AFFILIATEMARKETINGLLC@GMAIL.COM' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                <ProdDriftBadge />
              </div>
              <MarginHealthWidget />
              <a href="/admin/feedback" className="admin-feedback-link" data-testid="admin-feedback-link">
                📊 View All Feedback (Admin)
              </a>
            </>
          )}

          {/* Trial countdown / Upgrade nudge */}
          {(userInfo.plan === 'trial' || userInfo.plan === 'free') && (() => {
            let daysLeft = null;
            if (userInfo.trial_end) {
              const end = new Date(userInfo.trial_end);
              const ms = end - new Date();
              daysLeft = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
            }
            const label = daysLeft === null
              ? '⏳ Trial active · Choose your plan →'
              : daysLeft === 0
                ? '⚠️ Trial ends today · Pick a plan →'
                : daysLeft === 1
                  ? '⏳ 1 day left · Choose your plan →'
                  : `⏳ ${daysLeft} days left · Choose your plan →`;
            return (
              <a href="/pricing" className="trial-cta-banner" data-testid="trial-upgrade-banner">
                {label}
              </a>
            );
          })()}

          {/* Manage subscription (paying users) */}
          {userInfo.plan && !['trial', 'free'].includes(userInfo.plan) && (
            <button
              className="manage-billing-btn"
              onClick={openBillingPortal}
              data-testid="manage-billing-btn"
            >
              💳 Manage Subscription
            </button>
          )}

          {/* SuperBrain Builder — owner / admin / early-adopter only (not a customer feature) */}
          {(userInfo.is_admin || userInfo.is_early_adopter || (userInfo.email || '').toUpperCase() === 'A1AFFILIATEMARKETINGLLC@GMAIL.COM') && (
            <a href="/builder" className="builder-link" data-testid="builder-link">
              🚀 SuperBrain Builder
            </a>
          )}
        </div>

        {/* Invite & Earn */}
        {referral && (
          <div className="referral-card" data-testid="referral-card">
            <div className="referral-header">🎁 Invite & Earn</div>
            <div className="referral-reward">
              Earn <strong>1 week free</strong> for every friend who subscribes to a paid plan.
            </div>
            <div className="referral-stats">
              <span className="ref-stat" data-testid="ref-stat-total">
                {referral.stats.total} invited
              </span>
              <span className="ref-stat-divider">·</span>
              <span className="ref-stat" data-testid="ref-stat-rewarded">
                {referral.stats.rewarded} rewarded
              </span>
              <span className="ref-stat-divider">·</span>
              <span className="ref-stat ref-stat-earned" data-testid="ref-stat-weeks">
                {referral.stats.weeks_earned}w earned
              </span>
            </div>
            <div className="referral-code-row">
              <code className="referral-code" data-testid="referral-code">
                {referral.referral_code}
              </code>
              <button className="referral-copy-btn" onClick={copyReferralLink} data-testid="copy-referral-btn">
                {refCopied ? '✓ Copied' : 'Copy Link'}
              </button>
            </div>
            <a href={`/r/${referral.referral_code}`} className="referral-leaderboard-link" data-testid="copy-referral-share-hint" onClick={(e) => { e.preventDefault(); copyReferralLink(); }}>
              🎁 Share Your Invite Link →
            </a>
          </div>
        )}

        {/* Conversation History */}
        <div className="sessions-panel" data-testid="sessions-panel">
          <div className="sessions-header">
            <span>💬 Conversations</span>
            <button className="new-chat-btn" onClick={startNewChat} data-testid="new-chat-btn" title="New chat">
              + New
            </button>
          </div>
          <div className="sessions-list">
            {sessions.length === 0 && (
              <div className="sessions-empty">No chats yet</div>
            )}
            {sessions.slice(0, 15).map((s) => (
              <div
                key={s.session_id}
                className={`session-item ${currentSessionId === s.session_id ? 'active' : ''}`}
                onClick={() => loadSession(s.session_id)}
                data-testid={`session-${s.session_id}`}
                title={s.title}
              >
                <span className="session-title">{s.title}</span>
                <button
                  className="session-delete-btn"
                  onClick={(e) => deleteSession(s.session_id, e)}
                  data-testid={`delete-session-${s.session_id}`}
                  title="Delete"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="models-list">
          <h3>Active AI Providers</h3>
          {activeModels.map((model, idx) => (
            <div key={idx} className="model-item">
              <div 
                className={`model-indicator ${model.status}`}
                style={{ backgroundColor: model.status === 'active' ? model.color : '#555' }}
              />
              <span>{model.name}</span>
              <span className="model-status">{model.status}</span>
            </div>
          ))}
        </div>

        <button
          className="logout-button"
          onClick={() => (window.location.href = '/settings')}
          data-testid="settings-link"
          style={{ marginBottom: '8px', background: 'transparent', border: '1px solid rgba(212,175,55,0.4)', color: '#c9a84c' }}
        >
          Account Settings
        </button>

        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>

        <button 
          className="feedback-button" 
          onClick={() => setShowFeedbackModal(true)}
          title="Share your feedback with the admin"
        >
          💬 Feedback
        </button>
      </div>

      {/* Main Chat Area */}
      <div className="dashboard-main">
        <div className="top-bar">
          <div className="top-bar-title">
            SUPERBRAIN · AGENTIC AGGREGATE · ✝ GOD BLESS EVERYONE ✝
          </div>
          <div className="top-bar-actions">
            <LanguageSwitcher />
          </div>
        </div>

        <UseDisclaimerBanner />

        <MakeEveryQueryCountTip tierStatus={userInfo.tier_status} />

        <div className="messages-container">
          {messages.length === 0 && (
            <div className="welcome-message">
              <h2>Welcome to SuperBrain</h2>
              <p>Ask me anything. I'll query 6 AI providers and synthesize their responses with citations and a confidence score.</p>
              <div className="welcome-providers">
                <span>Claude</span>
                <span>GPT-5</span>
                <span>Gemini</span>
                <span>Grok</span>
                <span>Perplexity</span>
                <span>Mistral</span>
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div key={idx} className={`message ${msg.role}`}>
              {msg.role === 'user' && (
                <div className="message-content">
                  <div className="message-header">You</div>
                  <div className="message-text">{msg.content}</div>
                </div>
              )}
              
              {msg.role === 'assistant' && (
                <div className="message-content">
                  <div className="message-header">
                    <strong>SUPERBRAIN AGENTIC AGGREGATE</strong>
                    {msg.providers && (
                      <span className="providers-badge" data-testid="providers-badge">
                        {msg.providers.length} AIs synthesized
                      </span>
                    )}
                    {typeof msg.accuracyLabel === 'string' && (
                      <span
                        className={`accuracy-badge accuracy-${(msg.accuracyLabel || 'standard').toLowerCase()}`}
                        data-testid="accuracy-badge"
                        title={`Model-agreement estimate; not a measure of factual accuracy${msg.maxAccuracyMode ? ' · Citation Review Mode active' : ''}`}
                      >
                        {msg.maxAccuracyMode && '⚡ '}
                        Consensus estimate: {msg.accuracyLabel}
                      </span>
                    )}
                  </div>
                  <div className="message-text">{msg.content}</div>
                  {msg.dissentFlags && msg.dissentFlags.length > 0 && (
                    <div className="dissent-panel" data-testid="dissent-panel">
                      <div className="dissent-header">⚠️ Models disagreed on:</div>
                      <ul>
                        {msg.dissentFlags.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    </div>
                  )}
                  {msg.providers && (
                    <div className="message-providers">
                      {msg.providers.map((p, i) => (
                        <span key={i} className="provider-tag">{p}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {msg.role === 'error' && (
                <div className="message-content error">
                  <div className="message-header">Error</div>
                  <div className="message-text">{msg.content}</div>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="message-header">SUPERBRAIN</div>
                <div className="loading-indicator">
                  <span>Querying 6 AI providers</span>
                  <div className="loading-dots">
                    <span>.</span><span>.</span><span>.</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="input-area">
          <div className="input-toggles" data-testid="input-toggles">
            <label className={`toggle-chip ${maxAccuracyMode ? 'active' : ''}`} title="Citation-focused synthesis with uncertainty notes and disagreement flags">
              <input type="checkbox" checked={maxAccuracyMode}
                     onChange={(e) => setMaxAccuracyMode(e.target.checked)}
                     data-testid="max-accuracy-toggle" />
              <span>⚡ Citation Review Mode</span>
            </label>
            <label className={`toggle-chip ${fullPowerMode ? 'active' : ''}`} title="Force all 6 AI models regardless of query complexity">
              <input type="checkbox" checked={fullPowerMode}
                     onChange={(e) => setFullPowerMode(e.target.checked)}
                     data-testid="full-power-toggle" />
              <span>🧠 Full Power (6 AIs)</span>
            </label>
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask SuperBrain anything..."
            disabled={loading}
            rows={3}
            data-testid="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="send-button"
            data-testid="send-button"
          >
            {loading ? 'Processing...' : 'Send'}
          </button>
        </div>

        <div className="dashboard-footer">
          ✝ GOD BLESS EVERYONE ✝ · Semper Fidelis · © 2026 A-1 Affiliate Marketing LLC
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="feedback-modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h2>💬 Share Your Feedback</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowFeedbackModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="feedback-modal-body">
              <p>Help us improve SuperBrain! Your feedback goes directly to the admin.</p>

              <div className="feedback-type-selector">
                <label>
                  <input 
                    type="radio" 
                    value="comment" 
                    checked={feedbackType === 'comment'}
                    onChange={(e) => setFeedbackType(e.target.value)}
                  />
                  💭 Comment
                </label>
                <label>
                  <input 
                    type="radio" 
                    value="improvement" 
                    checked={feedbackType === 'improvement'}
                    onChange={(e) => setFeedbackType(e.target.value)}
                  />
                  💡 Improvement Idea
                </label>
                <label>
                  <input 
                    type="radio" 
                    value="bug" 
                    checked={feedbackType === 'bug'}
                    onChange={(e) => setFeedbackType(e.target.value)}
                  />
                  🐛 Bug Report
                </label>
              </div>

              <textarea
                className="feedback-textarea"
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder="Share your thoughts, suggestions, or report issues..."
                rows={6}
                maxLength={2000}
              />

              <div className="feedback-char-count">
                {feedbackMessage.length}/2000 characters
              </div>

              <div className="feedback-modal-actions">
                <button 
                  className="btn-cancel" 
                  onClick={() => setShowFeedbackModal(false)}
                  disabled={feedbackSubmitting}
                >
                  Cancel
                </button>
                <button 
                  className="btn-submit-feedback" 
                  onClick={submitFeedback}
                  disabled={feedbackSubmitting || !feedbackMessage.trim()}
                >
                  {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
